import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';
import { toApiError } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { SupportStartMismatchError } from '@/shared/auth/support/supportFailure';
import { supportStartMismatchProblem, supportStartProblem } from './supportStartModel';
import type { SupportTarget } from './useSupportStart';

export function useDirectSupportSwitch(onConfirmation: (target: SupportTarget) => void) {
  const { startSupport } = useAuth();
  const inFlight = useRef(false);
  const switchWorkspace = useMutation({
    mutationFn: (target: SupportTarget) => startSupport({ workspaceId: target.id }),
    networkMode: 'always',
    onSuccess: (_, target) => toast.success(`Sie arbeiten jetzt als Support in „${target.name}“.`),
    onError: async (error, target) => {
      if (error instanceof SupportStartMismatchError) {
        const problem = supportStartMismatchProblem(error);
        if (problem.kind === 'message') toast.error(problem.text);
        return;
      }
      const apiError = await toApiError(error);
      if (
        ['SupportSession.ConfirmationRequired', 'SupportSession.TwoFactorRequired'].includes(
          apiError.title,
        )
      ) {
        onConfirmation(target);
        return;
      }
      const problem = supportStartProblem(apiError);
      if (problem.kind !== 'two-factor-missing') toast.error(problem.text);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  return {
    pending: switchWorkspace.isPending,
    pick: (target: SupportTarget) => {
      if (inFlight.current) return;
      inFlight.current = true;
      switchWorkspace.mutate(target);
    },
  };
}
